import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  alpha,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  NotificationsOff,
  Check,
  DoneAll,
  Delete,
  Settings,
  Close,
} from '@mui/icons-material';
import { useNotifications } from '@context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const {
    isSupported,
    isInitialized,
    permission,
    isSubscribed,
    notifications,
    unreadCount,
    isLoading,
    requestPermission,
    subscribe,
    unsubscribe,
    markAsRead,
    markAllAsRead,
    sendTestNotification,
    getNotificationIcon,
    getNotificationColor,
  } = useNotifications();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showSettings, setShowSettings] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowSettings(false);
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    
    if (notification.url) {
      navigate(notification.url);
      handleClose();
    }
  };

  const handleEnableNotifications = async () => {
    if (permission === 'default') {
      const newPermission = await requestPermission();
      if (newPermission === 'granted') {
        await subscribe();
      }
    } else if (permission === 'granted') {
      await subscribe();
    }
  };

  const handleToggleSubscription = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await handleEnableNotifications();
    }
  };

  const handleSendTest = async () => {
    await sendTestNotification();
  };

  // If not supported, show disabled icon
  if (!isSupported) {
    return (
      <IconButton disabled sx={{ color: 'text.disabled' }}>
        <NotificationsOff />
      </IconButton>
    );
  }

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: unreadCount > 0 ? 'primary.main' : 'text.secondary',
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          overlap="circular"
        >
          {unreadCount > 0 ? <NotificationsActive /> : <Notifications />}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            mt: 1,
            borderRadius: 2,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAll />}
                onClick={markAllAsRead}
              >
                Mark all read
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Settings Panel */}
        {showSettings && (
          <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" gutterBottom>
              Notification Settings
            </Typography>

            {permission === 'denied' ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Notifications are blocked. Please enable them in your browser settings.
              </Alert>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isSubscribed}
                      onChange={handleToggleSubscription}
                      disabled={isLoading}
                    />
                  }
                  label={isSubscribed ? 'Notifications enabled' : 'Enable notifications'}
                />

                {isSubscribed && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSendTest}
                    sx={{ mt: 1 }}
                  >
                    Send Test Notification
                  </Button>
                )}
              </>
            )}

            <Divider sx={{ my: 1.5 }} />
          </Box>
        )}

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Enable Notifications Prompt */}
        {!isSubscribed && permission !== 'denied' && !isLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <NotificationsOff
              sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Stay updated with appointment reminders and important alerts
            </Typography>
            <Button
              variant="contained"
              onClick={handleEnableNotifications}
              sx={{ mt: 1 }}
            >
              Enable Notifications
            </Button>
          </Box>
        )}

        {/* Notification List */}
        {!isLoading && isSubscribed && (
          <>
            {notifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Notifications
                  sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  No notifications yet
                </Typography>
              </Box>
            ) : (
              <List sx={{ py: 0, maxHeight: 350, overflow: 'auto' }}>
                {notifications.slice(0, 10).map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      button
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        py: 1.5,
                        bgcolor: notification.isRead
                          ? 'transparent'
                          : alpha('#2196f3', 0.05),
                        '&:hover': {
                          bgcolor: notification.isRead
                            ? 'action.hover'
                            : alpha('#2196f3', 0.1),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(
                              getNotificationColor(notification.type),
                              0.1
                            ),
                            fontSize: '1.1rem',
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={notification.isRead ? 400 : 600}
                              noWrap
                            >
                              {notification.title}
                            </Typography>
                            {!notification.isRead && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              noWrap
                            >
                              {notification.body}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.disabled"
                            >
                              {formatDistanceToNow(
                                new Date(notification.createdAt),
                                { addSuffix: true }
                              )}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < notifications.length - 1 && (
                      <Divider component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}

            {/* View All Link */}
            {notifications.length > 10 && (
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  fullWidth
                  onClick={() => {
                    navigate('/notifications');
                    handleClose();
                  }}
                >
                  View All Notifications
                </Button>
              </Box>
            )}
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
